import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useToasts} from 'react-toast-notifications';
import {MapContext} from '../../../apiContexts';
import {reportDeviceColor, reportMarkerStep} from '../../../helpers/report';
import useDevices from '../../../hooks/use-devices';
import {getDeviceIcon, getDeviceIconColor} from '../../../helpers/deviceIcons';

const extractReplayPositions = (type, data) => {
  switch (type) {
    case 'route':
      return data[0]?.positions || [];
    case 'trip':
    case 'journey_trip':
      return data || [];
    default:
      return [];
  }
};

const resolveReplayDevice = (type, data, reportData, devices) => {
  if (type === 'route') return data[0]?.device;
  if (type === 'trip' || type === 'journey_trip') {
    return reportData.deviceId ? devices.findById(reportData.deviceId) : undefined;
  }
  return undefined;
};

export const resolveReplayIcon = (device, attributes = {}) => {
  if (!device?.category) return undefined;
  const color = getDeviceIconColor({status: 'online', ignition: attributes.ignition, alarm: attributes.alarm}) || 'blue';
  return getDeviceIcon(device.category, 'top', color);
};

const useMapReport = (state, dispatch, instance) => {
  const intl = useIntl();
  const {addToast} = useToasts();
  const [reportData, setReportData] = useState();
  const [replaying, setReplaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replayPositionId, setReplayPositionId] = useState(null);
  const {instance: map} = state;

  const replayPositionsRef = useRef([]);
  const replayDeviceRef = useRef(undefined);
  const replayIndexRef = useRef(0);
  const replayTimerRef = useRef(null);
  const {devices} = useDevices();

  const {setSelectedItem, setSelectedRow} = useContext(MapContext);
  useEffect(() => {
    setSelectedItem(null);
    setSelectedRow(null);
  }, []);

  useEffect(() => {
    if (state.instance) {
      map.initReportLayer();
      map.initReplayLayer();
    }
  }, [state.instance]);

  useEffect(() => {
    if (map && reportData) {
      const {type} = reportData;
      if (type !== 'journey_trip') {
        map.removeReports();
        map.instance.render();
        map.selectedReportPoint = null;
      }
    }
  }, [map, reportData]);

  useEffect(() => {
    if (reportData) {
      const {type, data, markers, deviceColors} = reportData;

      // a new selection always interrupts any replay in progress
      setReplaying(false);
      if (replayTimerRef.current) {
        clearTimeout(replayTimerRef.current);
        replayTimerRef.current = null;
      }
      map?.removeReplayMarker();
      replayIndexRef.current = 0;
      setReplayPositionId(null);
      replayPositionsRef.current = extractReplayPositions(type, data);
      replayDeviceRef.current = resolveReplayDevice(type, data, reportData, devices);

      // Quantas posições o resultado trouxe de fato -- o formulário não sabe isto antes de gerar.
      const totalPositions = type === 'route' ?
        data.reduce((total, {positions}) => total + positions.length, 0) :
        (data || []).length;
      const markerStep = markers ? reportMarkerStep(totalPositions) : 0;

      if (markerStep > 1) {
        addToast(
            intl.formatMessage(
                {id: 'reportMarkersSampled'},
                {count: totalPositions, step: markerStep},
            ),
            {appearance: 'info', autoDismiss: true},
        );
      }

      switch (type) {
        case 'route':
          // Um bloco por dispositivo, com a cor vinda da mesma tabela que a lista usa para pintar
          // o nome. A ordem só entra como reserva, se a tabela não vier.
          const positions = data.map(({device, positions}, indice) => ({
            deviceId: Number(device?.id),
            color: deviceColors?.get(Number(device?.id)) || reportDeviceColor(indice),
            positions: positions.map(({id, longitude, latitude, course}) => ({
              id,
              lon: longitude,
              lat: latitude,
              course,
            })),
          }));
          map.addReportRoute(positions, markerStep);
          break;
        case 'event':
        case 'stop':
          map.addReportPoint(data);
          break;
        case 'trip':
          map.addReportTrip(data, markerStep, reportData.id);
          map.fitReportExtent();
          break;
        case 'journey_init':
          data.forEach((stop) =>
            map.addReportPoint({
              id: stop.id,
              latitude: stop.startLat,
              longitude: stop.startLon,
            }),
          );
          break;
        case 'journey_trip':
          // addReportTrip calls removeReports() internally, so re-draw stops afterwards
          map.addReportTrip(data, false, reportData.id);
          (reportData.stops || []).forEach((stop) =>
            map.addReportPoint({
              id: stop.id,
              latitude: stop.startLat,
              longitude: stop.startLon,
            }),
          );
          map.fitReportExtent();
          break;
        default:
          map.removeReports();
          break;
      }
    }
  }, [state.instance, reportData]);

  const playReplay = useCallback(() => {
    if (!map || !replayPositionsRef.current.length) return;
    replayIndexRef.current = 0;
    setReplaying(true);
  }, [map]);

  const pauseReplay = useCallback(() => {
    setReplaying(false);
  }, []);

  useEffect(() => {
    if (!replaying) {
      if (replayTimerRef.current) {
        clearTimeout(replayTimerRef.current);
        replayTimerRef.current = null;
      }
      return undefined;
    }

    const step = () => {
      const positions = replayPositionsRef.current;
      const i = replayIndexRef.current;
      if (i >= positions.length) {
        setReplaying(false);
        return;
      }

      const {id, longitude, latitude, course, attributes} = positions[i];
      const coord = [longitude, latitude];
      const icon = resolveReplayIcon(replayDeviceRef.current, attributes);
      if (i === 0) {
        map.addReplayMarker(coord, course, icon);
      } else {
        map.updateReplayMarker(coord, course, icon);
      }
      setReplayPositionId(id);

      const next = positions[i + 1];
      replayIndexRef.current += 1;

      if (next) {
        // ATALHO: avança pelas posições reais, sem interpolar dentro do trecho entre duas delas;
        // usar LineString#getCoordinateAt se o movimento precisar ficar mais suave.
        const gapMs = new Date(next.fixTime) - new Date(positions[i].fixTime);
        const delay = Math.min(Math.max(gapMs / replaySpeed, 200), 3000);
        replayTimerRef.current = setTimeout(step, delay);
      }
    };

    step();

    return () => {
      if (replayTimerRef.current) {
        clearTimeout(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    };
  }, [replaying, replaySpeed, map]);

  return {
    setReportData, replaying, replaySpeed, playReplay, pauseReplay, setReplaySpeed, replayPositionId,
  };
};

export default useMapReport;
